import { WizardFlowContext } from "../../wizard-flow-context.model";
import { ContextBoundStepProperties } from "../context-bound-step-properties.model";
import { WizardStepBase } from "../wizard-step-base.model";

/**
 * Translate function signature: returns the translated string for a key in the
 * given language, or the key itself if no translation exists (standard
 * Transloco behaviour). Callers pass `translocoService.translate.bind(translocoService)`.
 */
export type StepLabelTranslator = (key: string, lang?: string) => string | undefined;

export class ContextInjector {

  public static availableKeys:string[] = [
    'borrower.firstName',
    'borrower.middleName',
    'borrower.lastName',
    'subjectProperty.address1',
    'subjectProperty.city',
    'subjectProperty.state',
    'subjectProperty.zipCode'
  ]

  /**
   * Resolver for step title/explanation/heading. Mirrors the classic
   * "explicit override wins for the active lang, else default for the active lang":
   *
   *   1. Admin `_i18n[lang][field]` override                 (explicit per-lang override)
   *   2. ONLY when viewing the default lang: `step[field]` IF it differs from the
   *      default-lang convention-key value (i.e. the admin actually customized it).
   *      LocalizedEditService writes edits made in the default lang to this slot.
   *      Non-default langs MUST NOT consider `step[field]` here — otherwise an EN
   *      customization would bleed into the ES UI.
   *   3. Convention-key default `wizard.${step.path}.${field}` for the active lang
   *   4. `step[field]` as last-resort fallback (covers no-translate-fn case and
   *      lang/convention combos where neither tier 1 nor tier 3 produced anything).
   *
   * Resolution must run BEFORE context-placeholder substitution so that
   * localized strings like "Hola [borrower.firstName]" have their placeholders
   * replaced on the translated text, not on a raw key.
   */
  private static resolveStepLabel(
    step: WizardStepBase,
    field: 'title' | 'explanation' | 'heading',
    lang: string | undefined,
    defaultLang: string | undefined,
    translate: StepLabelTranslator | undefined,
  ): string {
    // Tier 1: explicit per-language override
    if (lang) {
      const adminOverride = step._i18n?.[lang]?.[field];
      if (adminOverride) {
        return adminOverride;
      }
    }

    const base = step[field];
    const conventionKey = step.path ? `wizard.${step.path}.${field}` : undefined;
    const activeIsDefaultLang = !!lang && !!defaultLang && lang === defaultLang;

    // Tier 2: when viewing the default lang, the base IS the customization slot.
    // Return it unless we can prove it's just the unmodified convention default.
    if (activeIsDefaultLang && base) {
      if (translate && conventionKey) {
        const conventionDefault = translate(conventionKey, lang);
        const baseIsUncustomized =
          conventionDefault !== undefined &&
          conventionDefault !== conventionKey &&
          base === conventionDefault;
        if (!baseIsUncustomized) {
          return base;
        }
        // base == convention default → uncustomized → fall through to tier 3
      } else {
        // No translator/key info; preserve the base.
        return base;
      }
    }

    // Tier 3: convention-key default for the active lang
    if (lang && translate && conventionKey) {
      const translated = translate(conventionKey, lang);
      if (translated && translated !== conventionKey) {
        return translated;
      }
    }

    // Tier 4: last-resort fallback
    return base ?? '';
  }

  public static injectContextToStep = (
    wizardFlowContext: WizardFlowContext,
    step: WizardStepBase,
    lang?: string,
    translate?: StepLabelTranslator,
    defaultLang?: string,
  ): ContextBoundStepProperties => {
    const contextBoundStepProperties = new ContextBoundStepProperties();
    if (step) {
      contextBoundStepProperties.title = ContextInjector.resolveStepLabel(step, 'title', lang, defaultLang, translate);
      contextBoundStepProperties.explanation = ContextInjector.resolveStepLabel(step, 'explanation', lang, defaultLang, translate);
      contextBoundStepProperties.heading = ContextInjector.resolveStepLabel(step, 'heading', lang, defaultLang, translate);
    }
    ContextInjector.availableKeys.forEach(
      (key: string) => {
        const keyParts = key.split('.');
        if (keyParts.length === 2) {
          const objectIdentifier = keyParts[0];
          const fieldIdentifier = keyParts[1];
          let valueToReplaceKeyWith: string | undefined = undefined;
          let contextObject: any = undefined;
          if (objectIdentifier === "borrower") {
            contextObject = <any>wizardFlowContext.currentBorrower;
          } else if (objectIdentifier === "subjectProperty") {
            contextObject = <any>wizardFlowContext.mortgage.subjectProperty;
          }
          if (contextObject && fieldIdentifier) {
            valueToReplaceKeyWith = contextObject[fieldIdentifier];
            const keyToReplace = "[" + key + "]";
            if (valueToReplaceKeyWith) {
              if (contextBoundStepProperties.title.includes(keyToReplace)) {
                contextBoundStepProperties.title = contextBoundStepProperties.title.replace(keyToReplace, valueToReplaceKeyWith);
              }
              if (contextBoundStepProperties.explanation.includes(keyToReplace)) {
                contextBoundStepProperties.explanation = contextBoundStepProperties.explanation.replace(keyToReplace, valueToReplaceKeyWith);
              }
              if (contextBoundStepProperties.heading.includes(keyToReplace)) {
                contextBoundStepProperties.heading = contextBoundStepProperties.heading.replace(keyToReplace, valueToReplaceKeyWith);
              }
            }
          }
        }
      });
      return contextBoundStepProperties;
  }
}
